import React from 'react';
import {Card, CardActions, CardContent, CardHeader, Grid, styled, withStyles} from '@material-ui/core';
import {ArrowBackRounded, ArrowForwardRounded} from '@material-ui/icons';
import {autobind} from 'core-decorators';
import {reverse} from 'core/urls';
import Button from '../../../../components/button';
import Text from '../../../../components/text';
import styles from './styles.css';

const products = [
    {
        id: 694,
        name: 'Campera Shelby Montagne',
        className: 'campera-shelby-montagne',
    },
    {
        id: 701,
        name: 'Remera Polo Huapi (Mujer)',
        className: 'remera-polo-huapi-mujer',
    },
    {
        id: 703,
        name: 'Parrilla Lusqtoff',
        className: 'parrilla-lusqtoff',
    },
    {
        id: 696,
        name: 'Campera Elbrus Montagne (Hombre)',
        className: 'campera-elbrus-montagne-hombre',
    },
];

@autobind
class Carousel extends React.Component {
    // eslint-disable-next-line react/sort-comp
    carouselRef = React.createRef();
    rootRef = React.createRef();
    state = {
        scrollPosition: 0,
        scrollAmount: 0,
        rootWidth: 0,
        intervalId: 0,
    };
    componentDidMount() {
        const firstItem = this.carouselRef.current.firstChild;
        const intervalId = setInterval(() => {
            this.handleNext();
        }, 3000);
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
            scrollAmount: firstItem.clientWidth,
            rootWidth: this.rootRef.current.clientWidth,
            intervalId,
        });
    }
    componentWillUnmount() {
        clearInterval(this.state.intervalId);
    }

    handlePrevious() {
        const {scrollPosition, scrollAmount} = this.state;
        const newPosition = Math.max(scrollPosition - scrollAmount, 0);
        this.carouselRef.current.scrollTo({
            left: newPosition,
            behavior: 'smooth',
        });
        this.setState({scrollPosition: newPosition});
    }

    handleNext() {
        const {scrollPosition, scrollAmount} = this.state;
        const hasMorePages = this.hasMorePages();
        let newPosition;
        if (!hasMorePages) {
            newPosition = 0;
        } else {
            newPosition = Math.min(
                scrollPosition + scrollAmount,
                this.carouselRef.current.scrollWidth - this.carouselRef.current.clientWidth,
            );
        }
        this.carouselRef.current.scrollTo({
            left: newPosition,
            behavior: 'smooth',
        });
        this.setState({scrollPosition: newPosition});
    }

    hasMorePages() {
        if (this.carouselRef.current === null) return true;
        return this.state.scrollPosition + this.state.scrollAmount < this.carouselRef.current.scrollWidth;
    }

    render() {
        const {classes} = this.props;
        return (
            <div style={{position: 'relative'}}>
                <Root ref={this.rootRef}>
                    <div ref={this.carouselRef} className={classes.carouselContainer}>
                        {products.map(item => {
                            return (
                                <CarouselItem key={item.id} elevation={0} style={{width: `${this.state.rootWidth}px`}}>
                                    <Grid container style={{height: '100%'}}>
                                        <Grid item xs={12} sm={6}>
                                            <CardHeader
                                                title={<Text variant='h3' bold='bolder'>Marketing</Text>}
                                                disableTypography
                                            />
                                            <CardHeader
                                                style={{whiteSpace: 'normal'}}
                                                title={<Text style={{fontSize: '3.6rem'}}>{item.name}</Text>}
                                                disableTypography
                                            />
                                            <CardContent style={{whiteSpace: 'normal'}}>
                                                <Text variant='body1'>
                                                    {item.description || ''}
                                                </Text>
                                            </CardContent>
                                            <CardActions>
                                                <Button theme='gray' fullWidth link={reverse('productDetail', {id: item.id})}>
                                                    Ver producto
                                                </Button>
                                            </CardActions>
                                        </Grid>
                                        <Grid item xs={12} sm={6} style={{zIndex: 1}}>
                                            <BannerDiv className={styles[item.className]} />
                                        </Grid>
                                    </Grid>
                                </CarouselItem>
                            );
                        })}
                    </div>
                    <DecoratorDiv top='-200px' right='0' width='350px' height='400px' transform='rotate(70deg)' />
                    <DecoratorDiv bottom='-122px' right='-52px' width='500px' height='300px' transform='rotate(45deg)' />
                </Root>
                {this.state.scrollPosition > 0 && (
                    <FloattingButton position='left' onClick={this.handlePrevious}>
                        <ArrowBackRounded fontSize='large' />
                    </FloattingButton>
                )}
                {this.hasMorePages() && (
                    <FloattingButton position='right' onClick={this.handleNext}>
                        <ArrowForwardRounded fontSize='large' />
                    </FloattingButton>
                )}
            </div>
        );
    }
}

const BannerDiv = styled('div')(() => ({
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    height: '100%',
    width: '100%',
}));

const FloattingButton = styled('div')(({theme, ...props}) => ({
    width: theme.spacing(6),
    height: theme.spacing(6),
    borderRadius: '100%',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[200]}`,
    position: 'absolute',
    left: props.position === 'left' ? 0 : undefined,
    right: props.position === 'right' ? 0 : undefined,
    top: '50%',
    transform: `translateX(${props.position === 'left' ? '-' : ''}50%) translateY(-50%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
    },
}));

const DecoratorDiv = styled('div')(({theme, ...props}) => ({
    ...props,
    position: 'absolute',
    borderRadius: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
    mixBlendMode: 'multiply',
}));

const Root = styled('div')(({theme}) => ({
    position: 'relative',
    flexGrow: 1,
    margin: '0 auto',
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(4),
    overflow: 'hidden',
}));

/** @type {import('@material-ui/core').CardProps} */
const CarouselItem = withStyles(theme => ({
    root: {
        height: 400,
        minWidth: props => props.width || '100%',
        maxWidth: props => props.width || '100%',
        overflow: 'unset',
        padding: theme.spacing(5, 10),
        backgroundColor: 'unset',
    },
}))(Card);

export default withStyles((theme) => ({
    carouselContainer: {
        display: 'flex',
        overflowX: 'hidden',
        whiteSpace: 'nowrap',
        borderRadius: theme.spacing(4),
        backgroundColor: 'unset',
        zIndex: 1,
    },
}))(Carousel);



// WEBPACK FOOTER //
// ./views/home/supervisor-home/banners/index.js